/**
 * CLI Product Metrics Management
 *
 * Superadmin CRUD + read for first-class product metrics (metric_definitions),
 * scoped to the org the CLI user is logged into. Backed by `/superadmin/product-metrics/*`.
 * Lets an agent define a metric (postgres SQL or cloudwatch query), pull it on a
 * cadence, review recent values, and associate it with product board cards.
 */

import process from 'node:process';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import { apiRequest, fetchList } from './cli-helpers.js';

type MetricSource = 'postgres' | 'cloudwatch';

type CloudwatchConfig = {
  namespace: string;
  metricName: string;
  stat: string;
  periodSeconds: number;
  dimensions?: Record<string, string>;
};

type Metric = {
  id: string;
  name: string;
  title: string | null;
  description: string | null;
  source: MetricSource;
  query: string | null;
  cloudwatchConfig: CloudwatchConfig | null;
  unit: string | null;
  cadenceSeconds: number;
  enabled: boolean;
  lastValue: number | null;
  lastCollectedAt: string | null;
  lastError: string | null;
  cardIds: string[];
};

type DataPoint = { value: number | null; error: string | null; capturedAt: string };

type MetricApiResponse = {
  ok: boolean;
  error?: string;
  metric?: Metric;
  datapoint?: DataPoint | null;
  /** collect: true when the collector couldn't run in this environment. */
  skipped?: boolean;
  reason?: string;
};

function fmtValue(metric: Pick<Metric, 'lastValue' | 'unit' | 'lastError'>): string {
  if (metric.lastError) return `error: ${metric.lastError}`;
  if (metric.lastValue === null || metric.lastValue === undefined) return '—';
  return `${metric.lastValue}${metric.unit ? ` ${metric.unit}` : ''}`;
}

function formatMetric(metric: Metric): string {
  const status = metric.enabled ? '' : ' [disabled]';
  const cadence = `${metric.cadenceSeconds}s`;
  const cards = metric.cardIds.length > 0 ? ` cards=${metric.cardIds.length}` : '';
  const label = metric.title ? `${metric.title} (${metric.name})` : metric.name;
  return `  ${label}  (${metric.source}, every ${cadence}, latest=${fmtValue(metric)})${cards}${status}`;
}

async function listMetrics(apiUrl: string, token: string): Promise<Metric[]> {
  return fetchList<Metric>(apiUrl, token, '/superadmin/product-metrics', 'metrics');
}

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const metrics = await listMetrics(apiUrl, token);
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(metrics, null, 2));
    return;
  }
  if (metrics.length === 0) {
    console.log('No metrics found.');
    return;
  }
  for (const metric of metrics) console.log(formatMetric(metric));
}

async function handleGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const name = argv[0];
  if (!name || name.startsWith('--')) {
    console.error('Usage: cofactor metrics get <name> [--datapoints N] [--json]');
    process.exit(1);
  }
  const metrics = await listMetrics(apiUrl, token);
  const metric = metrics.find((mt) => mt.name === name);
  if (!metric) {
    console.error(`Error: metric '${name}' not found`);
    process.exit(1);
  }

  const nRaw = getArgValue(argv, '--datapoints');
  const n = nRaw ? Math.max(1, parseInt(nRaw, 10) || 0) : 0;
  const datapoints = n > 0
    ? await fetchList<DataPoint>(
        apiUrl,
        token,
        `/superadmin/product-metrics/${encodeURIComponent(name)}/datapoints`,
        'datapoints'
      )
    : [];

  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify({ metric, datapoints: datapoints.slice(-n) }, null, 2));
    return;
  }

  console.log(`${metric.name}${metric.enabled ? '' : ' [disabled]'}`);
  if (metric.title) console.log(`  title:    ${metric.title}`);
  if (metric.description) console.log(`  ${metric.description}`);
  console.log(`  source:   ${metric.source}`);
  if (metric.source === 'postgres') console.log(`  query:    ${metric.query ?? '(none)'}`);
  if (metric.source === 'cloudwatch') {
    console.log(`  cloudwatch: ${JSON.stringify(metric.cloudwatchConfig)}`);
  }
  console.log(`  cadence:  every ${metric.cadenceSeconds}s`);
  console.log(`  latest:   ${fmtValue(metric)}`);
  console.log(`  updated:  ${metric.lastCollectedAt ?? 'never'}`);
  if (metric.cardIds.length > 0) console.log(`  cards:    ${metric.cardIds.join(', ')}`);

  if (n > 0) {
    console.log(`  recent datapoints (last ${n}):`);
    for (const dp of datapoints.slice(-n)) {
      const v = dp.error ? `error: ${dp.error}` : dp.value;
      console.log(`    ${dp.capturedAt}  ${v}`);
    }
  }
}

/** Parse the cloudwatch flags into a config (or exit with a usage error). */
function parseCloudwatchConfig(argv: string[]): CloudwatchConfig {
  const namespace = getArgValue(argv, '--namespace');
  const metricName = getArgValue(argv, '--metric');
  const stat = getArgValue(argv, '--stat') ?? 'Average';
  const period = getArgValue(argv, '--period');
  if (!namespace || !metricName) {
    console.error('Error: cloudwatch metrics need --namespace and --metric.');
    process.exit(1);
  }
  const dimsRaw = getArgValue(argv, '--dimensions');
  let dimensions: Record<string, string> | undefined;
  if (dimsRaw) {
    dimensions = {};
    // Format: Name1=Value1,Name2=Value2
    for (const pair of dimsRaw.split(',')) {
      const [k, ...rest] = pair.split('=');
      if (k && rest.length > 0) dimensions[k.trim()] = rest.join('=').trim();
    }
  }
  return {
    namespace,
    metricName,
    stat,
    periodSeconds: period ? parseInt(period, 10) : 300,
    ...(dimensions ? { dimensions } : {}),
  };
}

async function handleCreate(argv: string[], apiUrl: string, token: string): Promise<void> {
  const name = argv[0];
  if (!name || name.startsWith('--')) {
    console.error('Error: Missing metric name.');
    console.error(
      'Usage: cofactor metrics create <name> --source <postgres|cloudwatch> --cadence <seconds> [...]'
    );
    process.exit(1);
  }
  const source = getArgValue(argv, '--source') as MetricSource | undefined;
  if (source !== 'postgres' && source !== 'cloudwatch') {
    console.error('Error: --source must be postgres or cloudwatch.');
    process.exit(1);
  }
  const cadenceRaw = getArgValue(argv, '--cadence');
  const cadenceSeconds = cadenceRaw ? parseInt(cadenceRaw, 10) : NaN;
  if (!Number.isInteger(cadenceSeconds) || cadenceSeconds < 60) {
    console.error('Error: --cadence <seconds> is required (minimum 60).');
    process.exit(1);
  }
  const title = getArgValue(argv, '--title');
  const description = getArgValue(argv, '--description');
  const unit = getArgValue(argv, '--unit');

  const body: Record<string, unknown> = {
    name,
    source,
    cadenceSeconds,
    ...(title !== undefined ? { title } : {}),
    ...(description !== undefined ? { description } : {}),
    ...(unit !== undefined ? { unit } : {}),
  };
  if (source === 'postgres') {
    const query = getArgValue(argv, '--query');
    if (!query) {
      console.error('Error: postgres metrics need --query "<read-only SQL returning one number>".');
      process.exit(1);
    }
    body.query = query;
  } else {
    body.cloudwatchConfig = parseCloudwatchConfig(argv);
  }

  const result = await apiRequest<MetricApiResponse>(
    apiUrl,
    token,
    'POST',
    '/superadmin/product-metrics',
    body
  );
  if (!result.ok || !result.metric) {
    console.error(`Error: ${result.error ?? 'Failed to create metric'}`);
    process.exit(1);
  }
  console.log(`Created metric ${result.metric.name} (${result.metric.source}, every ${result.metric.cadenceSeconds}s)`);
}

async function handleSet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const name = argv[0];
  if (!name || name.startsWith('--')) {
    console.error(
      'Usage: cofactor metrics set <name> [--title <text>] [--query <sql>] [--cadence <s>] [--unit <u>] [--description <text>] [--enable|--disable]'
    );
    process.exit(1);
  }
  const body: Record<string, unknown> = {};
  const title = getArgValue(argv, '--title');
  if (title !== undefined) body.title = title;
  const query = getArgValue(argv, '--query');
  if (query !== undefined) body.query = query;
  const cadenceRaw = getArgValue(argv, '--cadence');
  if (cadenceRaw !== undefined) body.cadenceSeconds = parseInt(cadenceRaw, 10);
  const unit = getArgValue(argv, '--unit');
  if (unit !== undefined) body.unit = unit;
  const description = getArgValue(argv, '--description');
  if (description !== undefined) body.description = description;
  if (hasFlag(argv, '--enable')) body.enabled = true;
  if (hasFlag(argv, '--disable')) body.enabled = false;

  if (Object.keys(body).length === 0) {
    console.error('Error: provide at least one field to update.');
    process.exit(1);
  }

  const result = await apiRequest<MetricApiResponse>(
    apiUrl,
    token,
    'PATCH',
    `/superadmin/product-metrics/${encodeURIComponent(name)}`,
    body
  );
  if (!result.ok || !result.metric) {
    console.error(`Error: ${result.error ?? 'Failed to update metric'}`);
    process.exit(1);
  }
  console.log(`Updated metric ${result.metric.name}`);
}

async function handleDelete(argv: string[], apiUrl: string, token: string): Promise<void> {
  const name = argv[0];
  if (!name || name.startsWith('--')) {
    console.error('Usage: cofactor metrics delete <name>');
    process.exit(1);
  }
  const result = await apiRequest<MetricApiResponse>(
    apiUrl,
    token,
    'DELETE',
    `/superadmin/product-metrics/${encodeURIComponent(name)}`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  console.log(`Deleted metric ${name}`);
}

async function handleCollect(argv: string[], apiUrl: string, token: string): Promise<void> {
  const name = argv[0];
  if (!name || name.startsWith('--')) {
    console.error('Usage: cofactor metrics collect <name>');
    process.exit(1);
  }
  const result = await apiRequest<MetricApiResponse>(
    apiUrl,
    token,
    'POST',
    `/superadmin/product-metrics/${encodeURIComponent(name)}/collect`
  );
  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Failed to collect metric'}`);
    process.exit(1);
  }
  if (result.skipped) {
    // Collector unavailable in this environment — metric state left untouched.
    console.log(`Skipped ${name}: ${result.reason ?? 'collector unavailable in this environment'}`);
    return;
  }
  const dp = result.datapoint;
  if (!dp) {
    console.error('Error: collect returned no datapoint');
    process.exit(1);
  }
  if (dp.error) {
    console.log(`Collected ${name}: error — ${dp.error}`);
  } else {
    console.log(`Collected ${name}: ${dp.value} (at ${dp.capturedAt})`);
  }
}

async function handleLink(argv: string[], apiUrl: string, token: string): Promise<void> {
  const name = argv[0];
  const cardId = getArgValue(argv, '--card');
  const clear = hasFlag(argv, '--clear');
  if (!name || name.startsWith('--') || !cardId) {
    console.error('Usage: cofactor metrics link <name> --card <cardId> [--clear]');
    process.exit(1);
  }
  const result = await apiRequest<MetricApiResponse>(
    apiUrl,
    token,
    'POST',
    `/superadmin/product-metrics/${encodeURIComponent(name)}/cards`,
    { cardId, action: clear ? 'unlink' : 'link' }
  );
  if (!result.ok) {
    console.error(`Error: ${result.error}`);
    process.exit(1);
  }
  console.log(
    clear ? `Unlinked metric ${name} from card ${cardId}` : `Linked metric ${name} to card ${cardId}`
  );
}

async function handleDatapoints(argv: string[], apiUrl: string, token: string): Promise<void> {
  const name = argv[0];
  if (!name || name.startsWith('--')) {
    console.error('Usage: cofactor metrics datapoints <name> [--since <ISO>] [--json]');
    process.exit(1);
  }
  const since = getArgValue(argv, '--since');
  const path = `/superadmin/product-metrics/${encodeURIComponent(name)}/datapoints${
    since ? `?since=${encodeURIComponent(since)}` : ''
  }`;
  const datapoints = await fetchList<DataPoint>(apiUrl, token, path, 'datapoints');
  if (hasFlag(argv, '--json')) {
    console.log(JSON.stringify(datapoints, null, 2));
    return;
  }
  if (datapoints.length === 0) {
    console.log('No datapoints.');
    return;
  }
  for (const dp of datapoints) {
    const v = dp.error ? `error: ${dp.error}` : dp.value;
    console.log(`  ${dp.capturedAt}  ${v}`);
  }
}

function printMetricsHelp(): void {
  console.log(
    [
      'Usage: cofactor metrics <sub-command> [options]',
      '',
      'Manage first-class product metrics for the org you are logged into.',
      '',
      'Sub-commands:',
      '  list [--json]                                 List metrics with latest value',
      '  get <name> [--datapoints N] [--json]          Show a metric + recent values',
      '  create <name> --source postgres --query "<SQL>" --cadence <s> [--title <t>] [--unit <u>] [--description <t>]',
      '  create <name> --source cloudwatch --namespace <ns> --metric <m> [--stat Average] [--period 300] [--dimensions K=V,K=V] --cadence <s>',
      '                                                Create a metric',
      '  set <name> [--title <t>] [--query <sql>] [--cadence <s>] [--unit <u>] [--description <t>] [--enable|--disable]',
      '                                                Update a metric',
      '  delete <name>                                 Delete a metric (and its datapoints)',
      '  collect <name>                                Pull the metric value now',
      '  link <name> --card <cardId> [--clear]         Link/unlink a product card',
      '  datapoints <name> [--since <ISO>] [--json]    Show the metric time series',
      '',
      'Sources: postgres (read-only SQL returning one number), cloudwatch (GetMetricData)',
      '',
      'Options:',
      '  --env <env>           Target environment (prod, dev, local)',
      '  --json                Output as JSON (list/get/datapoints)',
      '  --api-url <url>       API URL override (takes precedence over --env)',
      '  --token <key>         API token override',
    ].join('\n')
  );
}

export async function runMetrics(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printMetricsHelp();
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);

  switch (subCommand) {
    case 'list':
      await handleList(rest, apiUrl, token);
      break;
    case 'get':
      await handleGet(rest, apiUrl, token);
      break;
    case 'create':
      await handleCreate(rest, apiUrl, token);
      break;
    case 'set':
      await handleSet(rest, apiUrl, token);
      break;
    case 'delete':
      await handleDelete(rest, apiUrl, token);
      break;
    case 'collect':
      await handleCollect(rest, apiUrl, token);
      break;
    case 'link':
      await handleLink(rest, apiUrl, token);
      break;
    case 'datapoints':
      await handleDatapoints(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printMetricsHelp();
      process.exit(1);
  }
}
